import { api } from "@services/api"
import { isLoggedIn, logOut, updateUserState } from "@services/global-slice"

import {
  verifyEmailService,
  signUpService,
  fetchProfileService,
  isAuthorizedService,
  updateProfileService,
  getAllProfilesService,
  searchProfilesByEmailService,
  signInService,
  signOutService,
  updatePasswordService,
} from "./auth-service"

export const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    //addProfile
    addProfile: build.mutation({
      queryFn: async (body) => {
        const result = await new Promise((resolve) =>
          signUpService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )
        return { data: result }
      },
      invalidatesTags: ["Profile"],
    }),

    //verifyEmailService
    verifyEmail: build.mutation({
      queryFn: async (body) => {
        const result = await new Promise((resolve) =>
          verifyEmailService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )
        return { data: result }
      },
    }),

    //fetchProfile
    fetchProfile: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          fetchProfileService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                if (error) {
                  resolve({ data: null, error: error.response.data })
                }
              }
            })
        )
        return { data: result, error: result.error }
      },
    }),

    searchProfilesByEmail: build.query({
      queryFn: async ({ email }: { email: string }) => {
        const result: any = await new Promise((resolve) =>
          searchProfilesByEmailService(email)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response?.data })
              }
            })
        )
        return { data: result, error: result.error }
      },
    }),

    getAllProfiles: build.query({
      queryFn: async () => {
        const result: any = await new Promise((resolve) =>
          getAllProfilesService()
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                if (error) {
                  resolve({ data: null, error: error.response.data })
                }
              }
            })
        )
        return { data: result, error: result.error }
      },
      providesTags: ["AllProfiles"],
    }),

    //updateProfile
    updateProfile: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          updateProfileService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                if (error) {
                  resolve({ data: null, error: error.response.data })
                }
              }
            })
        )
        return { data: result, error: result.error }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled
        dispatch(updateUserState(arg)) // update redux state instead of invalidateTag
      },
    }),

    //updatePassword
    updatePassword: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          updatePasswordService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )
        return { data: result }
      },
      // invalidatesTag: ["Profile"] refreshes app
    }),

    //isAuthorized
    isAuthorized: build.query({
      queryFn: async () => {
        const result: any = await new Promise((resolve) =>
          isAuthorizedService()
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                if (error) {
                  resolve({ data: null, error: error.response?.data })
                }
              }
            })
        )
        return { data: result, error: result.error }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const data = await queryFulfilled
        dispatch(isLoggedIn(data))
      },
      providesTags: ["Auth", "Profile"],
    }),

    //signIn
    signIn: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          signInService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                if (error) {
                  resolve({ data: null, error: error.response?.data })
                }
              }
            })
        )
        return { data: result, error: result.error }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        const data = await queryFulfilled
        dispatch(isLoggedIn(data))
      },
      invalidatesTags: ["Auth", "Profile"],
    }),

    //signOut
    signOut: build.mutation({
      queryFn: async (_arg, _queryApi, extraOptions) => {
        const result = await signOutService()
        return { data: result }
      },
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // const data = await queryFulfilled
        dispatch(logOut())
      },
      invalidatesTags: ["Auth", "Profile"],
    }),
  }),
})

export const {
  useVerifyEmailMutation,
  useAddProfileMutation,
  useFetchProfileMutation,
  useUpdateProfileMutation,
  useIsAuthorizedQuery,
  useGetAllProfilesQuery,
  useSearchProfilesByEmailQuery,
  useSignInMutation,
  useSignOutMutation,
  useUpdatePasswordMutation,
} = authApi

/* 
SAMPLE IN UPDATEPASSWORD ENDPOINT 
Error response and handling in carpaddy2.0 
Send json from backend 
catch error in service and -> throw error 
catch error , if error and return it
return in slice ->   return { data: result } 
in app, check for result.error
*/
